import React, {Component} from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Button
} from 'reactstrap';
import ReactTable from 'react-table';
import {withTranslation} from 'react-i18next';
import IconCard from "../components/cards/IconCard";
import {Colxx} from '../components/common/CustomBootstrap';
import ModalManager from '../components/modal/ModalManager';
import {baseTokenSymbol, reactTableText} from '../constants/configs';
import {NotificationManager} from '../components/common/react-notifications';
import {fixedToWithoutRound, getTokenDecimals, numberWithCommas} from "../helpers/Utils";
import {filter, find, forEach, keyBy, mapValues, omit, reduce} from "lodash";
import ReactTableSelectFilter from "../components/ReactTableSelectFilter";
import InviteCodeForm from "./InviteCodeForm";
import Rbac from '../components/Rbac';
import {store} from "../AppRenderer";
import Api from '../services/Api';

class UserReferrals extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeReferral: null,
      referrals: [],
      loading: false,
      stats: {},
    };
    this.renderStatusFilter = this.renderStatusFilter.bind(this);
    this.renderTypeFilter = this.renderTypeFilter.bind(this);
  }

  componentDidMount() {
    if (Rbac.hasPermission("view_bonus-policy")) {
      this.fetchActiveReferral();
    }
    if (this.props.profile) {
      const {affiliateStats} = this.props.profile;
      this.setState(({stats}) => ({
        stats: {
          ...stats,
          totalReferred: affiliateStats.count,
          totalProfit: affiliateStats.totalProfit,
        },
      }));
    }
  }

  prepareData = referrals => {
    let newStats = {
      totalTradesCount: 0,
      totalTradesVolume: 0,
    };
    const userReferrals = filter(referrals, referral => !!referral.referCode);
    forEach(userReferrals, referral => {
      newStats = reduce(referral.stats.sums, (result, item) => {
        return {
          totalTradesCount: result.totalTradesCount + Number(item.num_trades),
          totalTradesVolume: result.totalTradesVolume + Number(item.total_trades),
        };
      }, newStats);
    });
    this.setState(({stats}) => ({
      referrals,
      stats: {
        ...stats,
        ...newStats,
      },
    }));
  };

  fetchData = async state => {
    this.setState({loading: true});
    try {
      const {data} = await Api.invoke(
        'GET',
        '/admin-ui/user-referrals',
        null,
        {
          params: {
            userId: this.props.userId,
            ...(state.filtered.length ? omit(mapValues(keyBy(state.filtered, 'id'), 'value'), 'createdAt') : null),
          }
        },
      );
      this.prepareData(data);
    } catch (e) {
      console.log('error: ', e);
    } finally {
      this.setState({
        loading: false,
      });
    }
  };

  fetchActiveReferral = async () => {
    const {userId} = this.props;
    try {
      const {data} = await Api.invoke(
        'GET',
        '/v2/admin-ui/bonusPolicies',
        null,
        {
          params: {
            user: userId,
            effectObject: "REFERRAL",
            status: "ACTIVATED"
          }
      });
      this.setState({
        activeReferral: data.rows[0],
      });
    } catch (e) {}
  };

  deleteReferral = async referCode => {
    const {t, userId} = this.props;
    try {
      await Api.invoke(
        'DELETE',
        '/admin-ui/user-referrals',
        {
          userId,
          referCode,
        },
      );
      setTimeout(this.fetchData, 1000);
      NotificationManager.success(t('Referral deleted successfully'), t('Message'));
    } catch (e) {
      console.log('error: ', e);
    }
  };

  onPressDelete = referCode => {
    const {t} = this.props;
    ModalManager.open({
      isOpen: true,
      title: t('Confirm Delete'),
      body: t('Are you sure you want to delete this referral ?'),
      buttons: [
        {
          color: 'primary',
          onClick: () => {
            this.deleteReferral(referCode);
            ModalManager.close({
              isOpen: false
            });
          },
          text: t('Yes'),
        },
        {
          color: 'grey',
          onClick: () => {
            ModalManager.close({
              isOpen: false
            });
          },
          text: t('No'),
        },
      ],
    });
  };

  onPressEdit = referral => {
    const {t, userId, maxPlanableReferral} = this.props;
    ModalManager.open({
      isOpen: true,
      title: t('Edit Referral'),
      body: <InviteCodeForm
        userId={userId}
        referral={referral}
        maxPlanableReferral={maxPlanableReferral}
        afterSubmit={() => {this.table.fireFetchData();}}
      />,
    });
  };

  onPressCreate = () => {
    const {t, userId, maxPlanableReferral} = this.props;
    ModalManager.open({
      isOpen: true,
      title: t('Create Invite Code'),
      body: <InviteCodeForm
        userId={userId}
        maxPlanableReferral={maxPlanableReferral}
        afterSubmit={() => {this.table.fireFetchData();}}
      />,
    });
  };

  getTokenLocaleName = symbol => {
    const {general} = store.getState();
    return find(general.tokens, item => item.symbol === symbol).localeName;
  };

  renderStatusFilter({filter, onChange}) {
    const {t} = this.props;
    const options = [
      {value: '', label: t('all')},
      {value: true, label: t('Enabled')},
      {value: false, label: t('Disabled')},
    ];
    return <ReactTableSelectFilter
      filter={filter}
      onChange={onChange}
      options={options}
    />;
  }

  renderTypeFilter({filter, onChange}) {
    const {t} = this.props;
    const options = [
      {value: '', label: t('all')},
      {value: "NORMAL", label: 'Normal'},
      {value: "UPGRADED", label: 'Upgraded'},
      {value: "DOWNGRADED", label: 'Downgraded'},
    ];
    return <ReactTableSelectFilter
      filter={filter}
      onChange={onChange}
      options={options}
    />;
  }

  getCurrentValue = (defaultValue, totalValue) => {
    const {activeReferral} = this.state;
    if (activeReferral) {
      const {effectFunction} = activeReferral;
      const limit = effectFunction.params.limit;
      return fixedToWithoutRound((Number(defaultValue) / totalValue) * Number(limit), 2);
    }
    return fixedToWithoutRound(Number(defaultValue), 2);
  };

  getColumns = t => [
    {
      Header: t('Plan Name'),
      accessor: 'name',
      className: 'font-weight-semibold',
      Cell: props => {
        let planName = props.value;
        if (props.original.creationType !== "NORMAL" && (planName.includes("upgraded") || planName.includes("downgraded"))) {
          const name = props.value.split("__");
          name.shift();
          planName = name.join("__");
        }
        if (planName === 'general') {
          return <span className='text-primary'>{t('Default Plan')}</span>;
        }
        if (planName === 'self' && !props.original.referCode) {
          return <span className='text-primary'>{t('Registered Plan')}</span>;
        }
        return (
          <span>{planName}</span>
        );
      },
    },
    {
      Header: t('Type'),
      accessor: 'creationType',
      filterable: true,
      Filter: this.renderTypeFilter,
    },
    {
      Header: t('Status'),
      accessor: 'active',
      filterable: true,
      Filter: this.renderStatusFilter,
      Cell: props => <span className={`font-weight-semibold ${props.value ? 'text-success' : 'text-danger'}`}>
        {props.value ? t("Enabled") : t("Disabled")}
      </span>
    },
    {
      Header: t('Invite Code'),
      accessor: 'referCode',
      Cell: props => <span>{props.value || '--------'}</span>
    },
    {
      Header: t('Get paid with'),
      accessor: 'token',
      Cell: props => <span>{props.value ? this.getTokenLocaleName(props.value) : "------"}</span>
    },
    {
      Header: t('User Share'),
      accessor: 'self',
      Cell: props => {
        const total = Number(props.value) + Number(props.original.user);
        return (
          <span>{`${Number(this.getCurrentValue(props.value, total))} %`}</span>
        )
      }
    },
    {
      Header: t('Friends Share'),
      accessor: 'user',
      Cell: props => {
        const total = Number(props.value) + Number(props.original.user);
        return (
          <span>{`${Number(this.getCurrentValue(props.value, total))} %`}</span>
        );
      }
    },
    {
      Header: t('Friends Count'),
      accessor: 'stats.num_referred',
      Cell: props => <span>{numberWithCommas(props.value)}</span>,
    },
    {
      Header: `${t('Friends Trades Volume')} (${t(baseTokenSymbol)})`,
      accessor: 'stats.sums',
      Cell: props => <span>
          {fixedToWithoutRound(reduce(props.value, (sum, item) => sum + Number(item.total_trades), 0), 0)}
        </span>,
    },
    {
      Header: t('Total Profits'),
      accessor: 'stats.sums',
      Cell: props => <span>
          {`${fixedToWithoutRound(
            reduce(
              props.value,
              (sum, item) => sum + Number(item.amount),
              0
            ),
            getTokenDecimals(props.original.token || baseTokenSymbol)
          )} ${this.getTokenLocaleName(props.original.token || baseTokenSymbol)}`}
        </span>,
      className: 'font-weight-semibold text-success',
      style: {
        fontSize: 14,
      }
    },
    {
      Header: t('Actions'),
      accessor: 'referCode',
      width: 85,
      Cell: props => {
        const {name, stats} = props.original;
        const {num_referred} = stats;
        if (name === 'general' || (name === 'self' && !props.value) || Number(num_referred) !== 0) {
          return null;
        }
        return (
          <div>
            <Rbac permission="delete_user-descriptions">
              <i
                className="simple-icon-trash delete-icon mx-3 cursor-pointer"
                onClick={() => {
                  this.onPressDelete(props.value);
                }}
              />
            </Rbac>
            <Rbac permission="update_user-descriptions">
              <i
                className="simple-icon-pencil edit-icon mx-3 cursor-pointer"
                onClick={() => {this.onPressEdit(props.original);}}
              />
            </Rbac>
          </div>
        );
      },
      show: Rbac.hasPermission('update_user-referrals') || Rbac.hasPermission('delete_user-referrals')
    },
  ];

  render() {
    const {t} = this.props;
    const {stats, loading, referrals} = this.state;
    const {totalProfit, totalReferred, totalTradesCount, totalTradesVolume} = stats;
    return (
      <Row>
        <Colxx xxs="12" className="mb-4">
          <Card className="h-100">
            <CardBody>
              <CardTitle className="text-large text-primary">
                {t('Referrals')}
              </CardTitle>
              <Row className='my-4'>
                <Col className='col-12 col-md-6 col-lg-3'>
                  {typeof totalProfit !== "undefined" &&
                    <IconCard
                      icon="icon-Money-Bag"
                      title={t('Total Referral Profit')}
                      value={t('{{toman}} Toman', {toman: fixedToWithoutRound(totalProfit, 0)})}
                    />
                  }
                </Col>
                <Col className='col-12 col-md-6 col-lg-3'>
                  {typeof totalReferred !== "undefined" &&
                    <IconCard
                      icon="icon-Business-Mens"
                      title={t('Friends Count')}
                      value={t('{{count}} person(s)', {count: fixedToWithoutRound(totalReferred, 0)})}
                    />
                  }
                </Col>
                <Col className='col-12 col-md-6 col-lg-3'>
                  {typeof totalTradesCount !== "undefined" &&
                    <IconCard
                      icon="icon-Sync"
                      title={t('Friends Trades Count')}
                      value={t('{{count}} trade(s)', {count: fixedToWithoutRound(totalTradesCount, 0)})}
                    />
                  }
                </Col>
                <Col className='col-12 col-md-6 col-lg-3'>
                  {typeof totalTradesVolume !== "undefined" &&
                    <IconCard
                      icon="icon-Money-2"
                      title={t('Friends Trades Volume')}
                      value={t('{{toman}} Toman', {toman: fixedToWithoutRound(totalTradesVolume, 0)})}
                    />
                  }
                </Col>
              </Row>
              <Rbac permission='create_user-referrals'>
                <div className='d-flex flex-row align-items-center justify-content-end mb-3'>
                  <Button onClick={this.onPressCreate}>
                    <i className="simple-icon-plus add-reply-icon"/>
                    <span className="mx-1">{t('Create Invite Code')}</span>
                  </Button>
                </div>
              </Rbac>
              <ReactTable
                manual
                ref={el => {this.table = el;}}
                className="-highlight"
                sortable={false}
                resizable={true}
                showPagination={false}
                data={referrals}
                loading={loading}
                defaultPageSize={referrals.length}
                onFetchData={this.fetchData}
                columns={this.getColumns(t)}
                style={{width: '100%'}}
                {...reactTableText}
              />
            </CardBody>
          </Card>
        </Colxx>
      </Row>
    );
  }
}

export default withTranslation()(UserReferrals);
